import { Box, Button } from '@mui/material';
import { AudioPlaybackInstance } from 'components/player/AudioPlayback';
import { Icons } from 'config/icons';
import { audioJournalFont, chapterIndexAt, readAlongPath } from 'helpers/audioJournal';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ApplicationState, Article, AudioChapter } from 'types';
import { ChapterList } from './ChapterList';
import { logEvent } from 'components/telemetry';
import { navigateEvent } from 'helpers/audioJournalAnalytics';

/** Chapter starts as seek-bar marks (none for a single chapter), and their look: small dots, darker on the played part. */
export const chapterMarks = (chapters: AudioChapter[]) => (chapters.length > 1 ? chapters.slice(1).map((c) => ({ value: c.start })) : undefined);
export const chapterMarksSx = {
	'& .MuiSlider-mark': { width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main', opacity: 0.6 },
	'& .MuiSlider-markActive': { backgroundColor: 'primary.dark', opacity: 1 },
};

const useCurrentTime = () => useSelector((state: ApplicationState) => state.tempState.audioPlaybackState.currentTime ?? 0);

/**
 * An audio journal's chapters in the full-screen player's playlist area, scrolled to the one playing.
 * Like the playlist, `scrollToItem()` brings the playing chapter back into view (from the "Next" bar).
 */
export const AudioJournalChapters = forwardRef(function AudioJournalChapters({ article }: { article: Article }, ref) {
	const currentTime = useCurrentTime();
	const chapters = article.audioJournal!.chapters;
	const current = chapterIndexAt(chapters, currentTime);
	const list = useRef<HTMLDivElement>(null);
	// Returns nothing: newer browsers' scrollIntoView returns a Promise, which an effect must not return.
	const scrollToCurrent = () => {
		list.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
	};

	useImperativeHandle(ref, () => ({ scrollToItem: scrollToCurrent }));
	useEffect(() => {
		scrollToCurrent();
	}, [current]);

	return (
		<Box ref={list} sx={{ height: '100%', overflowY: 'auto' }}>
			<ChapterList
				chapters={chapters}
				position={currentTime}
				font={audioJournalFont(article)}
				onSelect={(chapter) => {
					logEvent(...navigateEvent(article, 'chapter_list', chapter.start));
					AudioPlaybackInstance().seekTo(chapter.start);
				}}
			/>
		</Box>
	);
});

/** The chapter after the one playing, for the player's "Next" bar. */
export function NextChapter({ article }: { article: Article }) {
	const chapters = article.audioJournal!.chapters;
	const next = chapters[chapterIndexAt(chapters, useCurrentTime()) + 1];
	return next ? <span className={audioJournalFont(article)}>{next.title}</span> : <>None</>;
}

/** Opens the issue's PDF with read-along highlighting (when the issue has a read-along map). */
export function ReadAlongButton({ article }: { article: Article }) {
	const navigate = useNavigate();
	const info = article.audioJournal!;
	if (!info.syncFile) {
		return null;
	}
	return (
		<Button
			variant="outlined"
			size="small"
			startIcon={Icons.Publication}
			onClick={(event) => {
				event.stopPropagation();
				navigate(readAlongPath(info.editionId), { state: { isModal: true } });
			}}
		>
			Read along
		</Button>
	);
}
